/**
 * The app's names for the admin API schema. Every entity is an alias of the generated
 * `components['schemas']` in `./schema.d.ts` (`npm run gen:api` from the API's openapi.json),
 * so a schema change surfaces as a type error. Only query-parameter shapes and UI constants
 * are hand-written here.
 *
 * Note: utoipa emits `Option<T>` fields as optional (`field?: T | null`); treat a missing field
 * as null.
 */
import type { components } from './schema';

type S = components['schemas'];

// ---------- staff ----------
export type StaffRole = S['StaffRole'];
export type StaffUser = S['StaffUser'];
export type StaffTokens = S['StaffTokens'];
export type StaffInvite = S['StaffInvite'];
export type StaffList = S['StaffList'];

// ---------- common ----------
/**
 * One page of an admin list. `next_cursor` is opaque (keyset): pass it back as `cursor`, never
 * build or parse it, and drop it whenever `q` or a filter changes. `total` counts every row that
 * matches the filters, ignoring the cursor.
 */
export interface Page<T> {
  items: T[];
  next_cursor?: string | null;
  total: number;
}

/** Largest `limit` the admin list endpoints accept (default 50). */
export const MAX_PAGE_SIZE = 200;
/** Page size for list screens and pickers. */
export const PAGE_SIZE = 50;

export interface ListParams {
  q?: string;
  limit?: number;
  cursor?: string;
  /** Archivable lists: include archived rows (default false). */
  archived?: boolean;
}

/** List params without paging: what identifies one result set (and so one cursor chain). */
export type ListFilters<P extends ListParams> = Omit<P, 'cursor' | 'limit'>;

/** An uploaded image. `template` contains `{w}` and `{h}` placeholders. */
export type ImageRef = S['ImageRef'];
export type ImageKind = S['ImageKind'];
export type Problem = Partial<S['Problem']> & { [key: string]: unknown };
export type ValidationIssue = S['ValidationIssue'];

// ---------- creators ----------
export type AdminCreator = S['AdminCreator'];
export type CreatorInput = S['UpdateCreatorRequest'];
export type CreateCreatorInput = S['CreateCreatorRequest'];
export type CreatorRef = S['CreatorRef'];
export type CreatorPage = S['CreatorPage'];

// ---------- programmes ----------
export type ProgrammeKind = S['ProgrammeKind'];
export type ProgrammeStatus = S['ProgrammeStatus'];
export const PROGRAMME_STATUSES: ProgrammeStatus[] = [
  'draft',
  'in_review',
  'approved',
  'published',
  'archived',
];

export type ProgrammeListItem = S['ProgrammeListItem'];
export type ProgrammePage = S['ProgrammePage'];
export interface ProgrammeListParams extends ListParams {
  status?: ProgrammeStatus;
  kind?: ProgrammeKind;
  creator_id?: string;
}

export type ArtworkSlot = 'poster' | 'landscape' | 'thumb' | 'title_art';
export type ProgrammeArtwork = S['AdminArtwork'];
export type Rights = S['Rights'];
export type Vocabulary = S['Vocabulary'];
export const VOCABULARIES: Vocabulary[] = [
  'interest',
  'subject',
  'format',
  'style',
  'energy',
  'educational_value',
  'genre',
  'parent_objective',
];
export type TermRole = S['TermRole'];
export type ObjectiveStrength = S['ObjectiveStrength'];
export type ProgrammeTerm = S['ProgrammeTerm'];
export type ProgrammeObjective = S['ProgrammeObjective'];
export type ProgrammePreview = S['AdminPreview'];
export type AdminEpisode = S['AdminEpisode'];
export type AdminSeason = S['AdminSeason'];
export type CollectionRef = S['CollectionRef'];
export type AdminProgramme = S['AdminProgramme'];
export type CreateProgrammeInput = S['CreateProgrammeRequest'];
/** PATCH body (with If-Match). `status` is never accepted: use the transition endpoint. */
export type ProgrammePatch = S['ProgrammePatch'];

// ---------- seasons & episodes ----------
export type SeasonInput = S['UpdateSeasonRequest'];
export type CreateSeasonInput = S['CreateSeasonRequest'];
export type CreateEpisodeInput = S['CreateEpisodeRequest'];
export type EpisodePatch = S['UpdateEpisodeRequest'];

// ---------- taxonomy ----------
export type TaxonomyTerm = S['AdminTerm'];
export type TaxonomyInput = S['UpdateTermRequest'];
export type CreateTermInput = S['CreateTermRequest'];
export type TermPage = S['TermPage'];
export interface TermListParams extends ListParams {
  vocabulary?: Vocabulary;
}
/** A group of format terms: search matches any format within a group and all groups. */
export type FormatGroup = S['AdminFormatGroup'];
export type CreateFormatGroupInput = S['CreateFormatGroupRequest'];
export type FormatGroupInput = S['UpdateFormatGroupRequest'];

// ---------- collections ----------
export type CollectionKind = S['CollectionKind'];
export type LabelKind = S['LabelKind'];
export type CollectionItem = S['CollectionItem'];
export type AdminCollection = S['AdminCollection'];
export type CollectionInput = S['UpdateCollectionRequest'];
export type CreateCollectionInput = S['CreateCollectionRequest'];
export type CollectionPage = S['CollectionPage'];

// ---------- home layouts ----------
export type LayoutBand = 'default' | 'ages_2_4' | 'ages_5_7' | 'ages_8_10';
export const LAYOUT_BANDS: LayoutBand[] = ['default', 'ages_2_4', 'ages_5_7', 'ages_8_10'];
export type SlotKind = S['RowKind'];
export const SLOT_KINDS: SlotKind[] = [
  'hero',
  'continue',
  'selected',
  'because_you_like',
  'overlap',
  'surprise',
  'favourites',
  'collection',
  'recent',
];
export type HomeSlot = S['LayoutSlotView'];
export type HomeSlotInput = S['LayoutSlot'];
export type HomeLayout = S['HomeLayout'];

// ---------- videos & images ----------
export type VideoPurpose = S['VideoPurpose'];
export type VideoStatus = S['VideoStatus'];
export type VideoUsage = S['VideoUse'];
export type AdminVideo = S['AdminVideo'];
export type TusHeaders = S['TusHeaders'];
export type TusUpload = S['TusUpload'];
export type CreateVideoResponse = S['VideoUploadResponse'];
export type VideoPage = S['VideoPage'];
export interface VideoListParams extends ListParams {
  status?: VideoStatus;
  purpose?: VideoPurpose;
}
export type SignedHls = S['VideoPlayback'];
